import React, { useEffect } from "react";
import "./App.scss";
import { Route, Routes, Navigate } from "react-router-dom";
import AuthPage from "./pages/AuthPage/AuthPage";
import OrderProcessingPage from "./pages/OrderProcessingPage/OrderProcessingPage";
import PharmacistSupportPage from "./pages/PharmacistSupportPage/PharmacistSupportPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { loginCheckState } from "./store/auth-actions";
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsPharmacist,
  selectIsMarketplaceAdmin,
  selectCanViewAdminMarketplace,
  selectCanViewTransactions,
} from "./store/auth-slice";
import ArticlesOutletPage from "./pages/ArticlesOutletPage/ArticlesOutletPage";
import CommercialsPage from "./pages/CommercialsPage/CommercialsPage";
import Layout from "./components/Layout/Layout";
import ArticlesVisibilityPage from "./pages/ArticlesVisibilityPage/ArticlesVisibilityPage";
import OurPatientsPage from "./pages/OurPatientsPage/OurPatientsPage";
import PushNotificationsPage from "./pages/PushNotificationsPage/PushNotificationsPage";
import Statistics from "./pages/Statistics/Statistics";
import UnifiedReminderPage from "./pages/UnifiedReminderPage/UnifiedReminderPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import VideoConsultManager from "./components/VideoConsultManager/VideoConsultManager";
import ConsultOrderManager from "./components/ConsultOrder/ConsultOrderManager";
import SuspendedOrdersNotification from "./components/UI/SuspendedOrdersNotification/SuspendedOrdersNotification";
import TabNotificationManager from "./components/TabNotificationManager";
import AdminMarketPlacePage from "./pages/AdminMarketPlacePage/AdminMarketPlacePage";
import TransactionsPage from "./pages/TransactionsPage/Transactions";
// Loading component for authentication initialization
const AuthLoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
      fontFamily: "Arial, sans-serif",
      color: "#333",
    }}
  >
    Inicijalizacija autentifikacije...
  </div>
);

function App() {
  const dispatch = useDispatch();

  const canViewTransactions = useSelector(selectCanViewTransactions);

  // Authentication state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isPharmacist = useSelector(selectIsPharmacist);
  const isMarketplaceAdmin = useSelector(selectIsMarketplaceAdmin);
  const isAdmin = useSelector(selectIsAdmin);
  const canViewAdminMarketplace = useSelector(selectCanViewAdminMarketplace);
  const authInitialized = useSelector(
    (state: RootState) => state.auth.authInitialized,
  );

  // Initialize authentication on app start
  useEffect(() => {
    console.log("App: Initializing authentication system");
    dispatch<any>(loginCheckState());
  }, [dispatch]);

  // Show loading spinner during authentication initialization
  if (!authInitialized) {
    return <AuthLoadingSpinner />;
  }

  // Determine default route for authenticated users
  const getDefaultRoute = () => {
    if (isMarketplaceAdmin && !isAdmin) return "/admin-marketplace";
    return isPharmacist ? "/pharmacist-support" : "/order-processing";
  };

  // Conditional route rendering based on authentication state
  return (
    <>
      {isAuthenticated && <VideoConsultManager />}
      {isAuthenticated && <ConsultOrderManager />}
      {isAuthenticated && <SuspendedOrdersNotification />}
      {isAuthenticated && <TabNotificationManager />}

      <Layout>
        <Routes>
          {!isAuthenticated ? (
            // ==========================================
            // UNAUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/*" element={<AuthPage />} />
              {/* Redirect all other routes to auth */}
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </>
          ) : (
            // ==========================================
            // AUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route
                path="/auth"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
              <Route
                path="/auth/*"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
              <Route path="/profile" element={<ProfilePage />} />

              {isMarketplaceAdmin && !isAdmin ? (
                <>
                  <Route
                    path="/admin-marketplace"
                    element={
                      canViewAdminMarketplace ? (
                        <AdminMarketPlacePage />
                      ) : (
                        <Navigate to={getDefaultRoute()} replace />
                      )
                    }
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/admin-marketplace" replace />}
                  />
                </>
              ) : (
                <>
                  <Route
                    path="/order-processing"
                    element={<OrderProcessingPage />}
                  />
                  <Route
                    path="/pharmacist-support"
                    element={<PharmacistSupportPage />}
                  />
                  <Route
                    path="/articles-outlet"
                    element={<ArticlesOutletPage />}
                  />
                  <Route
                    path="/articles-visibility"
                    element={<ArticlesVisibilityPage />}
                  />
                  <Route
                    path="/web-commercials"
                    element={<CommercialsPage web />}
                  />
                  <Route
                    path="/mobile-commercials"
                    element={<CommercialsPage mobile />}
                  />
                  <Route
                    path="/rfzo-reminders"
                    element={<UnifiedReminderPage />}
                  />

                  <Route
                    path="/rfzo-patient-reminder"
                    element={
                      <Navigate to="/rfzo-reminders?view=patients" replace />
                    }
                  />
                  <Route
                    path="/rfzo-article-reminder"
                    element={
                      <Navigate to="/rfzo-reminders?view=aggregated" replace />
                    }
                  />

                  <Route path="/patients" element={<OurPatientsPage />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route
                    path="/transactions"
                    element={
                      canViewTransactions ? (
                        <TransactionsPage />
                      ) : (
                        <Navigate to={getDefaultRoute()} replace />
                      )
                    }
                  />
                  <Route
                    path="/push-notifications"
                    element={<PushNotificationsPage />}
                  />

                  <Route
                    path="*"
                    element={<Navigate to={getDefaultRoute()} replace />}
                  />
                </>
              )}
            </>
          )}
        </Routes>
      </Layout>
    </>
  );
}

export default App;
